import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon';
import { StylePicker } from '../components/StylePicker';
import { TemplatePicker } from '../components/TemplatePicker';
import { Avatar, HealthDot, PlanChip, StatusChip } from '../components/bits';
import { useMe, useOverview, useReload, useWorkspaceById } from '../data/store';
import { api } from '../data/api';
import { euro, shortDate, uptimeInfo, relFromNow } from '../data/helpers';
import type { Client, ClientInvoice, DomainStatus, ProvisionJob } from '../data/types';
import { DESIGN_STYLES, PREVIEW_BASE } from '../data/designStyles';
import { DESIGN_TEMPLATES } from '../data/designTemplates';

interface Props {
  client: Client;
}

const DOMAIN_LABEL: Record<DomainStatus, string> = {
  active: 'Attivo',
  pending: 'In verifica DNS',
  external: 'Gestito altrove',
  none: 'Non assegnato',
};

export function ClientView({ client: c }: Props) {
  const { activity } = useOverview();
  const workspaceById = useWorkspaceById();
  const reload = useReload();
  const isOperator = useMe()?.role === 'operator';
  const feed = activity.filter((a) => a.clientId === c.id);

  // "Apri editor sito" / "Apri portale" → impersonate the client's owner.
  // L'editor si apre DENTRO la Console (iframe a tutto schermo): la barra
  // dell'indirizzo resta admin.rismastudio.it, niente host "carlotta" in vista.
  const [opening, setOpening] = useState<'website' | 'portal' | null>(null);
  const [embed, setEmbed] = useState<{ url: string; label: string } | null>(null);
  const openPortal = async (view?: 'website') => {
    setOpening(view ?? 'portal');
    try {
      const { url } = await api.impersonate(c.slug, view);
      setEmbed({ url, label: (view ?? 'portal') === 'website' ? 'Editor del sito' : 'Portale gestione' });
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Impossibile aprire: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setOpening(null);
    }
  };
  const closeEmbed = () => {
    setEmbed(null);
    reload(); // i dati del cliente (pubblicato/modificato) possono essere cambiati
  };

  // Archiviazione soft: nasconde il cliente dalla Console (reversibile, nessun
  // dato cancellato). Per i clienti di prova. Restano sotto il filtro "Archiviati".
  const isArchived = c.status === 'archived';
  const [archiving, setArchiving] = useState(false);
  const toggleArchive = async () => {
    if (!isArchived && !confirm(`Archiviare "${c.name}"? Sparisce dalla lista (lo ritrovi nel filtro "Archiviati"). I dati restano.`)) return;
    setArchiving(true);
    try {
      await api.patchClient(c.slug, { archived: !isArchived });
      reload();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Operazione non riuscita: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setArchiving(false);
    }
  };

  // Eliminazione DEFINITIVA (irreversibile). Conferma forte: scrivi il nome.
  const [deleting, setDeleting] = useState(false);
  const del = async () => {
    // eslint-disable-next-line no-alert
    const typed = prompt(
      `ELIMINAZIONE DEFINITIVA di "${c.name}".\n\nNON è reversibile: sito, prenotazioni, clienti, contenuti — tutto cancellato.\n(Se il sito era già online, i file sul server vanno rimossi a parte.)\n\nScrivi il nome ESATTO del locale per confermare:`,
    );
    if (typed === null) return;
    if (typed.trim() !== c.name) {
      // eslint-disable-next-line no-alert
      alert('Nome non corrispondente — eliminazione annullata.');
      return;
    }
    setDeleting(true);
    try {
      await api.deleteClient(c.slug);
      reload(); // il cliente sparisce dalla lista → la Console torna indietro da sola
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Eliminazione non riuscita: ${e instanceof Error ? e.message : String(e)}`);
      setDeleting(false);
    }
  };

  // The list omits the live booking count; fetch it for Completo clients.
  const [bookings30, setBookings30] = useState<number | null>(c.bookings30);
  useEffect(() => {
    setBookings30(c.bookings30);
    if (c.plan === 'completo') {
      api
        .client(c.slug)
        .then((full) => setBookings30(full.bookings30))
        .catch(() => {});
    }
  }, [c.slug, c.plan, c.bookings30]);

  return (
    <div className="view">
      {embed && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(20,18,16,0.5)', display: 'flex', flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px',
              background: 'var(--panel)', color: 'var(--ink)',
              borderBottom: '1px solid rgba(26,22,18,0.12)',
            }}
          >
            <strong style={{ fontSize: 14 }}>{c.name}</strong>
            <span style={{ fontSize: 13, opacity: 0.6 }}>· {embed.label}</span>
            <div style={{ flex: 1 }} />
            <button type="button" className="btn btn-ghost btn-sm" onClick={closeEmbed}>
              <Icon name="x" size={14} /> Chiudi
            </button>
          </div>
          <iframe
            src={embed.url}
            title={embed.label}
            style={{ flex: 1, width: '100%', border: 0, background: '#fff' }}
          />
        </div>
      )}
      <div className="view-pad rs-stack">
        {/* header */}
        <div className="rs-card">
          <div className="rs-detail-head">
            <Avatar client={c} size={52} />
            <div className="rs-detail-id">
              <div className="rs-detail-name">{c.name}</div>
              <div className="rs-detail-meta">
                <span>{c.category} · {c.locality}</span>
                <StatusChip status={c.status} />
                <PlanChip plan={c.plan} />
                {c.isDemo && <span className="rs-chip demo">demo</span>}
                <HealthDot health={c.health} withLabel />
              </div>
            </div>
            <div className="rs-detail-actions">
              <button type="button" className="btn btn-primary" disabled={opening !== null} onClick={() => openPortal('website')}>
                <Icon name="edit" size={15} /> {opening === 'website' ? 'Apro…' : 'Apri editor sito'}
              </button>
              {c.plan === 'completo' && (
                <button type="button" className="btn btn-ghost" disabled={opening !== null} onClick={() => openPortal()}>
                  <Icon name="sliders" size={15} /> {opening === 'portal' ? 'Apro…' : 'Apri portale'}
                </button>
              )}
              {c.domainStatus === 'active' ? (
                <a className="btn btn-ghost" href={`https://${c.domain}`} target="_blank" rel="noreferrer">
                  <Icon name="globe" size={15} /> Vedi sito ↗
                </a>
              ) : (
                <button type="button" className="btn btn-ghost" disabled>
                  <Icon name="globe" size={15} /> Sito non online
                </button>
              )}
              <button type="button" className="btn btn-ghost btn-sm" disabled={archiving} onClick={toggleArchive} title={isArchived ? 'Riporta tra i clienti attivi' : 'Nascondi dalla Console (reversibile)'}>
                {archiving ? '…' : isArchived ? 'Ripristina' : 'Archivia'}
              </button>
              <button type="button" className="btn btn-ghost btn-sm text-danger" disabled={deleting} onClick={del} title="Eliminazione definitiva e irreversibile">
                {deleting ? '…' : 'Elimina'}
              </button>
            </div>
          </div>
        </div>

        <div className="rs-dgrid">
          {/* left */}
          <div className="rs-stack">
            <div className="rs-card">
              <div className="rs-card-head">
                <span className="rs-card-title">Sito</span>
              </div>
              <div className="rs-dl">
                <Row k="Dominio" v={c.domain === '—' ? '—' : c.domain} />
                <Row k="Stato dominio" v={DOMAIN_LABEL[c.domainStatus]} />
                <Row k="Ultima pubblicazione" v={shortDate(c.lastPublish)} />
                <Row k="Piano" v={c.plan === 'completo' ? 'Completo (sito + prenotazioni)' : 'Base (solo sito)'} />
              </div>
              {c.domain !== '—' && c.status === 'live' && (() => {
                const u = uptimeInfo(c.isUp, c.httpStatus);
                return (
                  <div className="rs-edit-note" data-uptime={u.state}>
                    <Icon name={u.state === 'offline' ? 'alert' : u.state === 'online' ? 'check' : 'globe'} size={15} />
                    <span>
                      <b>Stato sito: {u.label}.</b> {u.detail}
                      {c.lastCheckAt && <> <span className="rs-muted">Ultimo controllo {relFromNow(c.lastCheckAt)}.</span></>}
                    </span>
                  </div>
                );
              })()}
              <div className="rs-edit-note" data-edited={c.ownerEdited}>
                <Icon name={c.ownerEdited ? 'edit' : 'check'} size={15} />
                <span>
                  {c.ownerEdited ? (
                    <>
                      <b>Il cliente ha modificato il sito.</b> Le modifiche vanno fatte nell’editor — la sua copia ha
                      la priorità sul build file.
                    </>
                  ) : (
                    <>
                      <b>Mai modificato dal cliente.</b> Puoi modificare direttamente il build file nel repo.
                    </>
                  )}
                </span>
              </div>
            </div>

            {c.provisionManaged && <DomainCard key={`domain-${c.slug}`} c={c} onSaved={reload} />}
            {c.provisionManaged && <ProvisionCard slug={c.slug} />}

            <div className="rs-card">
              <div className="rs-card-head">
                <span className="rs-card-title">Attività</span>
              </div>
              {feed.length ? (
                <div className="rs-feed">
                  {feed.map((a) => (
                    <div className="rs-feed-item" key={a.id}>
                      <span className={'rs-feed-ico' + (a.kind === 'alert' ? ' warn' : '')}>
                        <Icon name={a.kind === 'alert' ? 'alert' : 'spark'} size={15} />
                      </span>
                      <span className="rs-feed-body">
                        <span className="rs-feed-text">{a.text}</span>
                        <span className="rs-feed-time">{a.time}</span>
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rs-muted" style={{ fontSize: 13 }}>Nessuna attività recente.</div>
              )}
            </div>

            {c.notes && (
              <div className="rs-card">
                <div className="rs-card-head">
                  <span className="rs-card-title">Note</span>
                </div>
                <div className="rs-note">{c.notes}</div>
              </div>
            )}
          </div>

          {/* right — lo Stile in cima: è la prima cosa che si decide per un
              cliente nuovo e per le bozze. Poi i Contatti (risaliti dal fondo,
              con "Gestito da") e le Prenotazioni; in fondo la fatturazione, che
              si tocca solo a periodi. */}
          <div className="rs-stack">
            <DesignCard key={`design-${c.slug}`} c={c} onSaved={reload} />
            <ContactsCard key={`contacts-${c.slug}`} c={c} managedBy={workspaceById(c.workspaceId)?.name ?? '—'} onSaved={reload} />

            {c.plan === 'completo' && (
              <div className="rs-card">
                <div className="rs-card-head">
                  <span className="rs-card-title">Prenotazioni</span>
                </div>
                <div className="rs-bigstat">
                  <span className="v">{bookings30 ?? 0}</span>
                  <span className="u">ultimi 30 giorni</span>
                </div>
              </div>
            )}

            <BillingCard key={c.slug} c={c} onSaved={reload} />
            {isOperator && <InvoicesCard key={`inv-${c.slug}`} c={c} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="rs-dl-row">
      <span className="rs-dl-k">{k}</span>
      <span className="rs-dl-v">{v}</span>
    </div>
  );
}

/** Editable billing — fee, demo toggle, billing identity + accountant email. */
function BillingCard({ c, onSaved }: { c: Client; onSaved: () => void }) {
  const [fee, setFee] = useState(String(c.monthlyFee));
  const [demo, setDemo] = useState(c.isDemo);
  const [billingName, setBillingName] = useState(c.billingName ?? '');
  const [vatNumber, setVatNumber] = useState(c.vatNumber ?? '');
  const [commEmail, setCommEmail] = useState(c.commercialistaEmail ?? '');
  const [busy, setBusy] = useState(false);

  const feeNum = Number(fee);
  const feeValid = fee.trim() !== '' && Number.isFinite(feeNum) && feeNum >= 0;
  const dirty =
    (feeValid && feeNum !== c.monthlyFee) ||
    demo !== c.isDemo ||
    billingName !== (c.billingName ?? '') ||
    vatNumber !== (c.vatNumber ?? '') ||
    commEmail !== (c.commercialistaEmail ?? '');

  const save = async () => {
    if (!dirty || busy) return;
    setBusy(true);
    try {
      await api.patchClient(c.slug, {
        monthlyFee: feeValid ? feeNum : undefined,
        isDemo: demo,
        billingName: billingName.trim() || null,
        vatNumber: vatNumber.trim() || null,
        commercialistaEmail: commEmail.trim() || null,
      });
      onSaved();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Errore: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const TextRow = ({ k, value, set, placeholder, type }: { k: string; value: string; set: (v: string) => void; placeholder: string; type?: string }) => (
    <div className="rs-dl-row">
      <span className="rs-dl-k">{k}</span>
      <span className="rs-dl-v">
        <input className="input rs-bill-input" type={type ?? 'text'} value={value} placeholder={placeholder} onChange={(e) => set(e.target.value)} />
      </span>
    </div>
  );

  return (
    <div className="rs-card">
      <div className="rs-card-head">
        <span className="rs-card-title">Fatturazione</span>
        {dirty && (
          <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={busy || !feeValid}>
            {busy ? 'Salvo…' : 'Salva'}
          </button>
        )}
      </div>
      <div className="rs-dl">
        <Row k="Piano" v={c.plan === 'completo' ? 'Completo' : 'Base'} />
        <div className="rs-dl-row">
          <span className="rs-dl-k">Canone mensile</span>
          <span className="rs-dl-v rs-fee">
            <input className="input rs-fee-input" type="number" min="0" step="0.01" value={fee} onChange={(e) => setFee(e.target.value)} />
            <span className="rs-fee-unit">€/mese</span>
          </span>
        </div>
        <div className="rs-dl-row">
          <span className="rs-dl-k">Account demo</span>
          <label className="rs-switch">
            <input type="checkbox" checked={demo} onChange={(e) => setDemo(e.target.checked)} />
            <span>{demo ? 'Sì · non conta come ricavo' : 'No'}</span>
          </label>
        </div>
        <TextRow k="Ragione sociale" value={billingName} set={setBillingName} placeholder={c.name} />
        <TextRow k="P.IVA" value={vatNumber} set={setVatNumber} placeholder="da visura" />
        <TextRow k="Email commercialista" value={commEmail} set={setCommEmail} placeholder="le fatture vanno anche a lui" type="email" />
        <Row k="MRR" v={c.isDemo ? '€0 · demo' : c.mrr ? `${euro(c.mrr)} / mese` : '—'} />
        <Row k="Setup" v={c.setupPaid ? 'Saldato' : 'Da incassare'} />
        <Row k="Cliente dal" v={shortDate(c.since)} />
      </div>
    </div>
  );
}

/** Contatti — titolare + telefono in sola lettura; email del titolare
 *  MODIFICABILE: serve a dare al proprietario un indirizzo raggiungibile col
 *  magic link (es. un cliente di prova creato con un'email finta). */
function ContactsCard({ c, managedBy, onSaved }: { c: Client; managedBy: string; onSaved: () => void }) {
  const [email, setEmail] = useState(c.owner.email);
  const [busy, setBusy] = useState(false);
  const trimmed = email.trim();
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  const dirty = valid && trimmed.toLowerCase() !== (c.owner.email ?? '').toLowerCase();

  const save = async () => {
    if (!dirty || busy) return;
    setBusy(true);
    try {
      await api.patchClient(c.slug, { ownerEmail: trimmed });
      onSaved();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Errore: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rs-card">
      <div className="rs-card-head">
        <span className="rs-card-title">Contatti</span>
        {dirty && (
          <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={busy}>
            {busy ? 'Salvo…' : 'Salva'}
          </button>
        )}
      </div>
      <div className="rs-dl">
        <Row k="Titolare" v={c.owner.name} />
        <div className="rs-dl-row">
          <span className="rs-dl-k">Email</span>
          <span className="rs-dl-v">
            <input className="input rs-bill-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </span>
        </div>
        {c.owner.phone && <Row k="Telefono" v={c.owner.phone} />}
        <Row k="Gestito da" v={managedBy} />
        <div className="rs-note" style={{ marginTop: 6 }}>
          Cambiare l’email permette al titolare di accedere col proprio magic link.
        </div>
      </div>
    </div>
  );
}

/** Invoices issued to this client (Fase 0: Risma bills — operator only). */
function InvoicesCard({ c }: { c: Client }) {
  const [list, setList] = useState<ClientInvoice[] | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.invoices(c.slug).then(setList).catch(() => setList([]));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.slug]);

  const create = async () => {
    const year = new Date().getFullYear();
    // eslint-disable-next-line no-alert
    const period = prompt('Periodo / descrizione della fattura:', `Canone annuale ${year} — sito e prenotazioni`);
    if (!period) return;
    // eslint-disable-next-line no-alert
    const amountStr = prompt('Importo (€):', String(Math.round(c.monthlyFee * 12 * 100) / 100));
    const amount = Number(amountStr);
    if (!amountStr || !Number.isFinite(amount) || amount < 0) return;
    const dest = [c.owner.email, c.commercialistaEmail].filter(Boolean).join(' + ');
    // eslint-disable-next-line no-alert
    if (!confirm(`Invio la fattura "${period}" (€${amount}) a: ${dest}?${c.commercialistaEmail ? '' : '\n\n⚠️ Email commercialista non impostata: andrà solo al cliente.'}`)) return;
    setBusy(true);
    try {
      await api.createInvoice(c.slug, { period: period.trim(), amount });
      await load();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Errore: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async (inv: ClientInvoice) => {
    await api.patchInvoice(inv.id, inv.status === 'paid' ? 'sent' : 'paid');
    await load();
  };

  return (
    <div className="rs-card">
      <div className="rs-card-head">
        <span className="rs-card-title">Fatture</span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={create} disabled={busy || c.isDemo}>
          {busy ? 'Invio…' : '+ Nuova fattura'}
        </button>
      </div>
      {!list ? (
        <div className="rs-muted" style={{ fontSize: 13 }}>Carico…</div>
      ) : list.length ? (
        <div className="rs-dl">
          {list.map((inv) => (
            <div className="rs-dl-row" key={inv.id}>
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span className="rs-dl-v" style={{ fontWeight: 600 }}>{inv.number} · {euro(inv.amount)}</span>
                <span className="rs-mini-meta">{inv.period} · {shortDate(inv.issuedOn)}</span>
              </span>
              <button
                type="button"
                className={'rs-chip ' + (inv.status === 'paid' ? 'st-ok' : 'st-work')}
                style={{ cursor: 'pointer', border: 0 }}
                title={inv.status === 'paid' ? 'Segna come non pagata' : 'Segna come pagata'}
                onClick={() => markPaid(inv)}
              >
                {inv.status === 'paid' ? 'Pagata' : 'Inviata'}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="rs-muted" style={{ fontSize: 13 }}>
          {c.isDemo ? 'Account demo — niente fatture.' : 'Nessuna fattura ancora. La prima parte con "Nuova fattura" — arriva al cliente e, se impostata, al suo commercialista.'}
        </div>
      )}
    </div>
  );
}

// ── DesignCard — lo "Stile" del sito (design preset, #38) ─────────────────
// Curato da Risma: carattere + palette + default hero/numeri. Il titolare
// resta su contenuti + i suoi 4 colori. Salvare rigenera il sito.
function DesignCard({ c, onSaved }: { c: Client; onSaved: () => void }) {
  const [style, setStyle] = useState(c.designStyle || 'classico');
  // '' = usa il default dello Stile; altrimenti override.
  const [hero, setHero] = useState<string>(c.heroLayout ?? '');
  const [num, setNum] = useState<string>(c.sectionNumerals == null ? '' : c.sectionNumerals ? 'on' : 'off');
  // Nome del locale (mostrato nell'hero del sito). Modificabile per correggere
  // errori di registrazione; lo slug/URL NON cambia.
  const [name, setName] = useState(c.name);
  // Tipo di locale (categoria) → etichetta accanto al nome sul sito; vuoto = niente.
  const [cat, setCat] = useState(c.categoryRaw ?? '');
  // Layout del menu (#39) — '' = default 'lista'.
  const [menu, setMenu] = useState<string>(c.menuLayout ?? '');
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Template = scheletro della pagina (ortogonale allo Stile). Operatore-scelto.
  const [template, setTemplate] = useState(c.template || 'classico');
  const [tplOpen, setTplOpen] = useState(false);
  // Testi-firma del template (es. Lampo: manifesto/marquee/striscia) — li scegliamo
  // NOI in Console, non il cliente. Vuoti → il sito usa i default di design.
  const [tplCfg, setTplCfg] = useState<Record<string, string | boolean>>(c.templateConfig || {});
  // I testi sono stringhe (trim, vuoto→via); i toggle sono booleani: tengo solo true
  // (nascosto), scarto false (= mostrato = default) così la config resta minima.
  const cleanCfg = (o: Record<string, string | boolean>) =>
    Object.fromEntries(
      Object.entries(o)
        .map(([k, v]) => [k, typeof v === 'boolean' ? v : (v || '').trim()] as [string, string | boolean])
        .filter(([, v]) => v),
    ) as Record<string, string | boolean>;

  const dirty =
    (name.trim() !== '' && name.trim() !== c.name) ||
    style !== (c.designStyle || 'classico') ||
    template !== (c.template || 'classico') ||
    hero !== (c.heroLayout ?? '') ||
    num !== (c.sectionNumerals == null ? '' : c.sectionNumerals ? 'on' : 'off') ||
    cat.trim() !== (c.categoryRaw ?? '') ||
    menu !== (c.menuLayout ?? '') ||
    JSON.stringify(cleanCfg(tplCfg)) !== JSON.stringify(c.templateConfig || {});

  const previewUrl = (() => {
    const p = new URLSearchParams({ style, slug: c.slug });
    if (template && template !== 'classico') p.set('template', template);
    // Config-firma del template (testi Lampo, toggle heroSlideshow del Classico…)
    // → anteprima dal vivo prima del salvataggio, per QUALSIASI template.
    const cc = cleanCfg(tplCfg);
    if (Object.keys(cc).length) p.set('tcfg', JSON.stringify(cc));
    if (hero) p.set('hero', hero);
    if (num) p.set('numerals', num);
    if (menu) p.set('menu', menu);
    return `${PREVIEW_BASE}/preview?${p.toString()}`;
  })();

  const save = async () => {
    if (!dirty || busy) return;
    setBusy(true);
    try {
      await api.patchClient(c.slug, {
        name: name.trim() || c.name,
        category: cat.trim() || null,
        designStyle: style,
        template,
        templateConfig: cleanCfg(tplCfg),
        heroLayout: hero ? (hero as 'split' | 'cover' | 'type-only' | 'centro') : null,
        sectionNumerals: num === '' ? null : num === 'on',
        menuLayout: menu ? menu : null,
      });
      onSaved();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Errore: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const suited = DESIGN_STYLES.find((s) => s.id === style)?.suited;

  return (
    <div className="rs-card">
      <div className="rs-card-head">
        <span className="rs-card-title">Stile del sito</span>
        {dirty && (
          <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={busy}>
            {busy ? 'Salvo…' : 'Salva e rigenera'}
          </button>
        )}
      </div>
      <div className="rs-dl">
        <div className="rs-dl-row">
          <span className="rs-dl-k">Nome del locale</span>
          <span className="rs-dl-v">
            <input className="input rs-bill-input" value={name} placeholder="Nome mostrato nell'hero del sito"
              onChange={(e) => setName(e.target.value)} />
          </span>
        </div>
        <div className="rs-dl-row">
          <span className="rs-dl-k">Tipo di locale</span>
          <span className="rs-dl-v">
            <input className="input rs-bill-input" value={cat} placeholder="Es. Osteria — vuoto = niente"
              onChange={(e) => setCat(e.target.value)} />
          </span>
        </div>
        <div className="rs-dl-row">
          <span className="rs-dl-k">Template</span>
          <span className="rs-dl-v">
            <button type="button" className="input rs-bill-input sp-trigger" onClick={() => setTplOpen(true)} title="Sfoglia i template (lo scheletro del sito)">
              <span>{DESIGN_TEMPLATES.find((t) => t.id === template)?.label ?? template}</span>
              <Icon name="cdown" size={14} />
            </button>
          </span>
        </div>
        {/* Stile/Testata/Numeri/Layout-menu valgono per il Classico; i template
            bold (Lampo, Carta, Marea) hanno hero, menu, font e palette propri →
            per quelli mostriamo solo una nota colori. */}
        {template !== 'lampo' && template !== 'carta' && template !== 'marea' ? (
        <>
        <div className="rs-dl-row">
          <span className="rs-dl-k">Stile</span>
          <span className="rs-dl-v">
            <button type="button" className="input rs-bill-input sp-trigger" onClick={() => setPickerOpen(true)} title="Sfoglia gli stili con anteprima">
              <span>{DESIGN_STYLES.find((s) => s.id === style)?.label ?? style}</span>
              <Icon name="cdown" size={14} />
            </button>
          </span>
        </div>
        <div className="rs-dl-row">
          <span className="rs-dl-k">Testata</span>
          <span className="rs-dl-v">
            <select className="input rs-bill-input" value={hero} onChange={(e) => setHero(e.target.value)}>
              <option value="">Default dello stile</option>
              <option value="split">Affiancata</option>
              <option value="cover">A tutta pagina</option>
              <option value="centro">Logo al centro</option>
              <option value="type-only">Solo testo</option>
            </select>
          </span>
        </div>
        <div className="rs-dl-row">
          <span className="rs-dl-k">Numeri sezioni</span>
          <span className="rs-dl-v">
            <select className="input rs-bill-input" value={num} onChange={(e) => setNum(e.target.value)}>
              <option value="">Default dello stile</option>
              <option value="on">Sì (01 · 02…)</option>
              <option value="off">No</option>
            </select>
          </span>
        </div>
        <div className="rs-dl-row">
          <span className="rs-dl-k">Layout menu</span>
          <span className="rs-dl-v">
            <select className="input rs-bill-input" value={menu} onChange={(e) => setMenu(e.target.value)}>
              <option value="">Lista (default)</option>
              <option value="portate">A portate (tab / carosello)</option>
              <option value="fisarmonica">Fisarmonica</option>
              <option value="colonne">Colonne</option>
              <option value="essenziale">Essenziale</option>
              <option value="foto">Con foto</option>
            </select>
          </span>
        </div>
        <div className="rs-dl-row">
          <span className="rs-dl-k">Foto testata</span>
          <span className="rs-dl-v">
            <select className="input rs-bill-input" value={tplCfg.heroSlideshow === true ? 'slideshow' : 'single'}
              onChange={(e) => setTplCfg({ ...tplCfg, heroSlideshow: e.target.value === 'slideshow' })}>
              <option value="single">Una foto (o secondo la testata)</option>
              <option value="slideshow">Più foto che si alternano (a tutta pagina)</option>
            </select>
          </span>
        </div>
        </>
        ) : (
          <div className="rs-dl-row">
            <span className="rs-dl-k">Colori</span>
            <span className="rs-dl-v rs-muted" style={{ fontSize: 12, alignSelf: 'center' }}>
              {template === 'carta'
                ? 'Carta parte coi suoi colori (carta crema + rosso); il titolare può cambiarli dal suo editor.'
                : template === 'marea'
                ? 'Marea parte coi suoi colori (notte-mare + oro); il titolare può cambiarli dal suo editor — meglio restare su fondi scuri.'
                : 'Lampo parte coi suoi colori (rosso/giallo); il titolare può cambiarli dal suo editor.'}
            </span>
          </div>
        )}
      </div>
      {template === 'lampo' && (
        <div style={{ marginTop: 12, display: 'grid', gap: 8, paddingTop: 12, borderTop: '1px solid var(--rule)' }}>
          <span className="rs-card-title" style={{ fontSize: 13 }}>Testi di Lampo</span>
          <span className="rs-muted" style={{ fontSize: 12, marginTop: -4 }}>
            I testi-firma del template — li scegli tu, non il cliente. Vuoto = default di design.
          </span>
          <label style={{ display: 'grid', gap: 3 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Simbolo nella barra</span>
            <select className="input rs-bill-input" value={(tplCfg.mark as string) || 'bolt'}
              onChange={(e) => setTplCfg({ ...tplCfg, mark: e.target.value })}>
              <option value="bolt">Fulmine (default)</option>
              <option value="logo">Logo dell'attività</option>
            </select>
            <span className="rs-muted" style={{ fontSize: 11 }}>Con "Logo" serve un logo caricato dal titolare; altrimenti resta il fulmine.</span>
          </label>
          {[
            { key: 'manifestoEyebrow', label: 'Manifesto · occhiello', ph: 'Niente fronzoli' },
            { key: 'manifestoLine', label: 'Manifesto · riga', ph: 'SOLO ROBA' },
            { key: 'manifestoHighlight', label: 'Manifesto · parola accesa', ph: 'BUONA.' },
            { key: 'marquee', label: 'Nastro in alto (marquee)', ph: 'auto: tipo · claim' },
            { key: 'strip', label: 'Striscia rossa', ph: "★ DOPPIO SMASH ★ PATATINE LOADED ★ …" },
            { key: 'captions', label: 'Didascalie foto (separate da virgola)', ph: 'doppio smash, patatine loaded, …' },
          ].map((f) => (
            <label key={f.key} style={{ display: 'grid', gap: 3 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{f.label}</span>
              <input className="input rs-bill-input" value={(tplCfg[f.key] as string) ?? ''} placeholder={f.ph}
                onChange={(e) => setTplCfg({ ...tplCfg, [f.key]: e.target.value })} />
            </label>
          ))}
          {/* Righe scorrevoli: spunta = mostrata. Togliendo la spunta si scrive il flag *Off=true. */}
          {[
            { key: 'marqueeOff', label: 'Nastro scorrevole in alto', on: 'Mostrato', off: 'Nascosto' },
            { key: 'stripOff', label: 'Striscia rossa scorrevole', on: 'Mostrata', off: 'Nascosta' },
            { key: 'heroStill', label: 'Foto dell’hero', on: 'In movimento (si alternano)', off: 'Ferma (una sola)' },
          ].map((t) => (
            <label key={t.key} style={{ display: 'grid', gap: 3 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{t.label}</span>
              <label className="rs-switch">
                <input type="checkbox" checked={tplCfg[t.key] !== true}
                  onChange={(e) => setTplCfg({ ...tplCfg, [t.key]: !e.target.checked })} />
                <span>{tplCfg[t.key] === true ? t.off : t.on}</span>
              </label>
            </label>
          ))}
        </div>
      )}
      <div className="rs-note" style={{ marginTop: 10 }}>
        {suited && <>Adatto a: {suited}. </>}
        <a href={previewUrl} target="_blank" rel="noreferrer">Anteprima ↗</a> — il titolare cura contenuti e i suoi 4 colori.
      </div>
      {pickerOpen && (
        <StylePicker
          current={style}
          onSelect={(id) => {
            setStyle(id);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {tplOpen && (
        <TemplatePicker
          current={template}
          onSelect={(id) => {
            setTemplate(id);
            setTplOpen(false);
          }}
          onClose={() => setTplOpen(false)}
        />
      )}
    </div>
  );
}

// ── DomainCard — il dominio del sito, modificabile prima di "Crea sito" ────
// Il dominio si imposta alla creazione, ma va corretto qui (es. Luigi mette
// "ancoranuova.it" — non ancora registrato → certbot fallisce; si passa a un
// sottodominio di rismastudio.it che risolve già al droplet). Cambiarlo NON
// rigenera i contenuti: dopo il salvataggio si rilancia "Crea sito" qui sotto.
function DomainCard({ c, onSaved }: { c: Client; onSaved: () => void }) {
  const current = c.domain === '—' ? '' : c.domain;
  const [domain, setDomain] = useState(current);
  const [busy, setBusy] = useState(false);
  const d = domain.trim().toLowerCase();
  // Hostname puro: etichette [a-z0-9-] (no trattino iniziale/finale) separate da
  // punti, almeno due etichette. Specchio di isValidHostname lato API.
  const valid = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/.test(d);
  const dirty = d !== '' && d !== current;

  const save = async () => {
    if (!dirty || !valid || busy) return;
    setBusy(true);
    try {
      await api.patchClient(c.slug, { marketingDomain: d });
      onSaved();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Errore: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rs-card">
      <div className="rs-card-head">
        <span className="rs-card-title">Dominio del sito</span>
        {dirty && valid && (
          <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={busy}>
            {busy ? 'Salvo…' : 'Salva dominio'}
          </button>
        )}
      </div>
      <div className="rs-dl">
        <div className="rs-dl-row">
          <span className="rs-dl-k">Indirizzo</span>
          <span className="rs-dl-v">
            <input className="input rs-bill-input" value={domain} placeholder="es. ancoranuova.rismastudio.it"
              onChange={(e) => setDomain(e.target.value)} />
          </span>
        </div>
        {dirty && !valid && (
          <p className="rs-dim" style={{ fontSize: 12, margin: '4px 0 0' }}>
            Solo minuscole, numeri, punti e trattini (es. <code>nome.rismastudio.it</code>).
          </p>
        )}
        <p className="rs-dim" style={{ fontSize: 12, margin: '8px 0 0', lineHeight: 1.5 }}>
          Per mettere online adesso usa un <b>sottodominio di rismastudio.it</b> (es.{' '}
          <code>ancoranuova.rismastudio.it</code>): risolve già al server, il certificato esce subito. Il dominio
          del cliente si mette dopo, quando è registrato e punta al server. Salvato il dominio, rilancia{' '}
          <b>“Crea sito”</b> qui sotto.
        </p>
      </div>
    </div>
  );
}

// ── ProvisionCard — il bottone "Crea sito" del robot (Opzione C) ──────────
// Mostra l'ultimo lavoro della coda e lo polla mentre è in corso. La
// provision mette online sito+prenotazioni+portale; il rebuild rigenera i
// contenuti (parte anche da solo quando il titolare pubblica dall'editor).
function ProvisionCard({ slug }: { slug: string }) {
  const [job, setJob] = useState<ProvisionJob | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showLog, setShowLog] = useState(false);

  const active = job?.status === 'queued' || job?.status === 'running';
  // Bumpato dopo aver avviato un job per ri-armare il poll (che si ferma da solo
  // a job concluso/assente).
  const [pollKey, setPollKey] = useState(0);

  // Carica l'ultimo job; poi polla ogni 3s SOLO finché è attivo
  // (queued/running). A job concluso o assente il poll si ferma — niente
  // richieste all'infinito su una scheda lasciata aperta.
  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = () =>
      api
        .provisionStatus(slug)
        .then((j) => {
          if (!alive) return;
          setJob(j);
          setLoaded(true);
          if (j && (j.status === 'queued' || j.status === 'running')) {
            timer = setTimeout(tick, 3000);
          }
        })
        .catch(() => alive && setLoaded(true));
    tick();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [slug, pollKey]);

  const start = async (kind: 'provision' | 'rebuild') => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const j = await api.provisionStart(slug, kind);
      setJob(j);
      setShowLog(true);
      setPollKey((k) => k + 1); // ri-arma il poll per seguire il job
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore imprevisto.');
    } finally {
      setBusy(false);
    }
  };

  const STATUS_LABEL: Record<ProvisionJob['status'], string> = {
    queued: 'In coda…',
    running: 'In lavorazione…',
    done: 'Completato',
    error: 'Errore',
  };

  return (
    <div className="rs-card">
      <div className="rs-card-head">
        <span className="rs-card-title">Robot · messa online</span>
        {job && (
          <span className={`rs-chip ${job.status === 'done' ? 'ok' : job.status === 'error' ? 'danger' : ''}`}>
            {job.kind === 'provision' ? 'Crea sito' : 'Rigenera'} — {STATUS_LABEL[job.status]}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!loaded || busy || active}
          onClick={() => start('provision')}
        >
          <Icon name="globe" size={14} />
          {active ? 'In corso…' : job?.status === 'done' ? 'Ri-provisiona' : 'Crea sito'}
        </button>
        {job?.status === 'done' && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={busy || active}
            onClick={() => start('rebuild')}
          >
            <Icon name="edit" size={14} /> Rigenera contenuti
          </button>
        )}
        {job?.log && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowLog((v) => !v)}>
            {showLog ? 'Nascondi log' : 'Mostra log'}
          </button>
        )}
      </div>

      {err && (
        <div style={{ marginTop: 10, fontSize: 13, color: 'var(--danger)' }}>{err}</div>
      )}
      {job?.status === 'error' && job.error && (
        <div style={{ marginTop: 10, fontSize: 13, color: 'var(--danger)' }}>
          <b>Il robot si è fermato:</b> {job.error.split('\n')[0]}
        </div>
      )}

      {showLog && job?.log && (
        <pre
          style={{
            marginTop: 10,
            padding: '10px 12px',
            background: '#221d17',
            color: '#f1e9da',
            borderRadius: 8,
            fontSize: 11.5,
            lineHeight: 1.6,
            maxHeight: 220,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
          }}
        >
          {job.log.split('\n').slice(-40).join('\n')}
        </pre>
      )}

      <div className="rs-edit-note" style={{ marginTop: 10 }}>
        <Icon name="spark" size={15} />
        <span>
          La <b>provision</b> richiede contenuto pubblicato (orari e telefono inclusi) nell'editor "Sito
          web". Il <b>rebuild</b> parte da solo a ogni pubblicazione del titolare.
        </span>
      </div>
    </div>
  );
}
